import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector';
import React from 'react';

import {
    ErrorBoundary,
    Facet,
    Paging,
    PagingInfo,
    Results,
    ResultsPerPage,
    SearchBox,
    SearchProvider,
    Sorting,
    WithSearch,
} from '@elastic/react-search-ui';
import { Layout } from '@elastic/react-search-ui-views';
import '@elastic/react-search-ui-views/lib/styles/styles.css';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    buildAutocompleteQueryConfig,
    buildFacetConfigFromConfig,
    buildSearchOptionsFromConfig,
    buildSortOptionsFromConfig,
    getConfig,
    getFacetFields,
} from '../../config/config-helper';
import rgOfferPlaceholderSmall from '../../doc/img/dummy_small.png';
import { sanitizeHtml } from '../../utils/commonFunctions';
import { withLangParam } from '../../utils/language';
import './elastic-custom-ui.css';
import MultiCheckboxFacet from './MultiCheckboxFacet';

const { hostIdentifier, searchKey, endpointBase, engineName } = getConfig();
const connector = new AppSearchAPIConnector({
    searchKey,
    engineName,
    hostIdentifier,
    endpointBase,
});
const config = {
    searchQuery: {
        facets: buildFacetConfigFromConfig(),
        ...buildSearchOptionsFromConfig(),
    },
    autocompleteQuery: buildAutocompleteQueryConfig(),
    apiConnector: connector,
    alwaysSearchOnInitialLoad: true,
    initialState: {
        sortField: 'publish_at',
        sortDirection: 'desc',
    },
    routingOptions: {
        // search-ui rewrites the whole query string, so keep the lang param in it
        writeUrl: (url, { replaceUrl = false } = {}) => {
            const search = withLangParam(url, i18n.language);
            const updateHistory = replaceUrl
                ? window.history.replaceState
                : window.history.pushState;
            updateHistory.call(
                window.history,
                null,
                '',
                `${window.location.pathname}${search}`
            );
        },
    },
};

const CustomResultView = ({ result, onClickLink }) => {
    return (
        <li>
            <div className="single_post post_type12 type20">
                <div className="post_img">
                    <div className="img_wrap">
                        {result && result.image && result.image.raw ? (
                            <img src={result.image.raw} alt="" />
                        ) : (
                            <img
                                className="result_thumbnail"
                                src={rgOfferPlaceholderSmall}
                                alt=""
                            />
                        )}
                    </div>
                </div>
                <div className="single_post_text">
                    <h3>
                        {result && result.title && result.url && (
                            <a onClick={onClickLink} href={result.url.raw}>
                                {result.title.raw}
                            </a>
                        )}
                    </h3>
                    <div>
                        {result &&
                            result.description &&
                            result.description.snippet && (
                                <div
                                    className="post-p"
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(
                                            result.description.snippet
                                        ),
                                    }}
                                />
                            )}
                    </div>
                </div>
            </div>
        </li>
    );
};

/**
 * NewSearchPage Component
 * - Implements a custom search interface powered by Elastic App Search.
 * - Configures facets, sorting, autocomplete, and search query parameters dynamically.
 * - Provides a custom result view for displaying search results.
 * - Includes pagination, results per page, and sorting options.
 * - Utilizes ErrorBoundary for error handling within the search UI.
 */
export default function NewSearchPage() {
    const { t } = useTranslation();

    return (
        <div className="container">
            <SearchProvider config={config}>
                <WithSearch
                    mapContextToProps={({ wasSearched }) => ({ wasSearched })}
                >
                    {({ wasSearched }) => {
                        return (
                            <div className="App">
                                <ErrorBoundary>
                                    <Layout
                                        header={
                                            <SearchBox
                                                autocompleteSuggestions="true"
                                                inputView={({
                                                    getAutocomplete,
                                                    getInputProps,
                                                    getButtonProps,
                                                }) => (
                                                    <>
                                                        <div className="sui-search-box__wrapper">
                                                            <input
                                                                // eslint-disable-next-line react/jsx-props-no-spreading
                                                                {...getInputProps(
                                                                    {
                                                                        placeholder: t(
                                                                            'searchPage.searchInput.placeholder'
                                                                        ),
                                                                    }
                                                                )}
                                                            />
                                                            {getAutocomplete()}
                                                        </div>
                                                        <input
                                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                                            {...getButtonProps({
                                                                value: t(
                                                                    'searchPage.searchInput.submitButton'
                                                                ),
                                                            })}
                                                        />
                                                    </>
                                                )}
                                            />
                                        }
                                        sideContent={
                                            <div>
                                                {wasSearched && (
                                                    <Sorting
                                                        label="Sort by"
                                                        sortOptions={buildSortOptionsFromConfig()}
                                                    />
                                                )}
                                                {getFacetFields().map(
                                                    (field) => (
                                                        <Facet
                                                            view={
                                                                MultiCheckboxFacet
                                                            }
                                                            key={field}
                                                            field={field}
                                                            label={field.replaceAll(
                                                                '_',
                                                                ' '
                                                            )}
                                                        />
                                                    )
                                                )}
                                            </div>
                                        }
                                        bodyContent={
                                            <Results
                                                resultView={CustomResultView}
                                            />
                                        }
                                        bodyHeader={
                                            <>
                                                {wasSearched && <PagingInfo />}
                                                {wasSearched && (
                                                    <ResultsPerPage />
                                                )}
                                            </>
                                        }
                                        bodyFooter={<Paging />}
                                    />
                                </ErrorBoundary>
                            </div>
                        );
                    }}
                </WithSearch>
            </SearchProvider>
        </div>
    );
}
